/**
 * Centralized mutable state for the game UI.
 * All state lives here — no other module should have module-level let variables.
 */

const state = {
  app: null,
  currentState: null,
  previousState: null,
  animating: false,
  discardSelection: new Set(),
  bidAnimatedCount: 0,
  passedPositions: new Set(),
  activeBidNumbers: new Map(), // gamePosition → { value, punctuation }
  hintTimer: null,
  pendingSlotFadeIn: false,
  promptCommit: {
    pending: false,
    error: null,
  },
  hadApiError: false,
  intentionalClear: false,
  trumpRevealPending: false,
  dispatchTailClaims: 0,
  dispatchTailIdleWaiters: [], // resolve fns parked by whenDispatchTailIdle
  chainHalted: false,
  bidPromptOverride: null, // { value, punctuation, dismissed } | null — see getBidPromptOverride
  pendingDispatchEvents: 0,
  boardBurstShaped: false,
};

// --- App root ---

export function getApp() {
  return state.app;
}
export function setApp(el) {
  state.app = el;
}

// --- Game state ---

export function getCurrentState() {
  return state.currentState;
}
export function getPreviousState() {
  return state.previousState;
}
export function setCurrentState(s) {
  state.previousState = s;
  state.currentState = s;
}
export function advanceCurrentState(s) {
  state.currentState = s;
}

// --- Animation lock ---

// Callback injection (set once in game.init): fired when the lock clears (true→false)
// so a resize skipped while an animation held the lock can replay against the settled
// board — completion renders don't cover animations that end awaiting human input.
let onAnimationSettled = null;
export function setOnAnimationSettled(fn) {
  onAnimationSettled = fn;
}

// Callback injection (set once in game.init): fired with the new lock value whenever it
// actually transitions (true<->false, never on a redundant same-value set) — mirrors the
// lock to a DOM attribute (game.js#mirrorAnimatingToDom) so burst-scoped `will-change`
// (docs/PERFORMANCE.md) can key off it without state.js owning DOM.
let onAnimatingChanged = null;
export function setOnAnimatingChanged(fn) {
  onAnimatingChanged = fn;
}

export function isAnimating() {
  return state.animating;
}
export function setAnimating(v) {
  const was = state.animating;
  state.animating = v;
  if (was !== v) onAnimatingChanged?.(v);
  if (!v) {
    state.promptCommit.pending = false;
    if (was) onAnimationSettled?.();
  }
}

// --- Dispatch tail lock ---

// True while anything is claimed on dispatch.js's `tail`: a burst mid-mutation, a burst still
// QUEUED behind other tail work, and the round-start bid replay's own chained beat
// (dispatch.js#chainOnDispatchTail). Distinct from isAnimating(): recoverFromApiError
// force-clears the animation lock, and the between-burst window leaves it false — but a stray
// render() landing on an occupied tail tears down the persistent nodes that work owns, and
// whatever is queued behind it then re-applies its own (older) state over the render's board
// (resync-render-lands-mid-bid-replay: a gap-timeout resync landing mid-replay left the
// client resting at the pre-click bidding state while the server had moved on). api.js's
// renderFetchedState and boot.js's routeFetchRender read this to skip such a render; the tail
// is authoritative.
//
// A claim count, not a boolean, because dispatch.js claims SYNCHRONOUSLY at call time and
// releases only when that chained work settles — so claims overlap and only the last release
// frees the tail. A beat that never settles leaves this busy forever; that is the same wedged
// tail docs/dom/MUTATIONS.md already accepts (reload rebuilds from the server's state).
export function isDispatchTailBusy() {
  return state.dispatchTailClaims > 0;
}
export function claimDispatchTail() {
  state.dispatchTailClaims += 1;
}
export function releaseDispatchTail() {
  // Clamped like decrementPendingDispatchEvents, and for that counter's reason: resetState() is a
  // test-lifecycle reset (no production caller), so a suite that resets between beats would
  // otherwise drive this negative — which reads as "free" either way, but stops being a count.
  state.dispatchTailClaims = Math.max(0, state.dispatchTailClaims - 1);
  if (state.dispatchTailClaims === 0) settleDispatchTailIdleWaiters();
}

// Resolves at a moment when nothing holds the tail — NOT "on the next drop to zero". The
// difference is the whole contract: api.js#showGameStart's teardown awaits this after its fade,
// and a claim can still appear between the release that woke a waiter and that waiter's
// resumption. An HTTP response sent before the quit is the concrete case — closePushStream() and
// clearCurrentGame() don't cancel one in flight, and a plain api() submit or submitBidPipelined
// (the bid pipeline bypasses the isAnimating() guard, so a bid click is live all through the
// replay) lands on applyServerState → onAfterApi → runDispatch → chainOnDispatchTail. The
// re-check therefore lives inside the primitive, where the count lives: no caller can resolve on
// a stale drop to zero. What it deliberately does NOT promise is that the tail is still free once
// the caller's own continuation runs — a claim can land in the microtask hops between this last
// check and whatever the caller does next, so a caller whose work must actually LAND re-tests
// afterwards and waits again (api.js#showGameStart's teardown loops on isDispatchTailBusy). An
// already-free tail returns without parking a waiter — still one microtask turn, since this is
// async — and the loop body never runs.
//
// api.js cannot ask dispatch.js this directly (dispatch.js → bidding-mutations.js → bid-queue.js
// → bid-pipeline.js → api.js), but both import this module — the same reason the claim count
// lives here rather than beside the tail it counts.
export async function whenDispatchTailIdle() {
  while (isDispatchTailBusy()) {
    await new Promise((resolve) => state.dispatchTailIdleWaiters.push(resolve));
  }
}

// The list is swapped out before resolving: a waiter that re-parks (the tail was re-claimed in
// the meantime) must land on the fresh list, not on the one being drained.
function settleDispatchTailIdleWaiters() {
  const waiters = state.dispatchTailIdleWaiters;
  state.dispatchTailIdleWaiters = [];
  for (const resolve of waiters) resolve();
}

// --- Board burst-shaped invariant (the reconcile precondition) ---

// True once a mutation burst's resting commit has shaped the persistent board to its
// prior rest — the state in which a phase's restingCommit(S) reproduces what render(S)
// would build, WITHOUT rebuilding the nodes. The eventless-mid-game-continue reconcile
// (animation-controller.js#renderWithAnimation) reads this to decide it is safe to commit
// in place instead of a destructive full render. Set true in dispatch.js#drainBurst right
// after the resting commit; cleared by every from-scratch render() (game.js), which leaves
// the board render-shaped — possibly at a pre-reveal interstitial the commit can't reconcile
// (the reload-mid-pause path). The flag, not a pendingAction whitelist, is what makes the
// reconcile safe across all phases: it is false exactly when the board is NOT burst-shaped.
export function getBoardBurstShaped() {
  return state.boardBurstShaped;
}
export function setBoardBurstShaped(v) {
  state.boardBurstShaped = v;
}

// --- Chain halt (dispatch fail-loud) ---

// True once recoverFromFatalError has frozen the board — for the reload it then triggers itself,
// or (loop guard tripped) behind the last-resort dialog. routeAndDispatch swallows the burst
// rejection (the deliberate fail-loud model: never fall through to a full render), so the ingest
// can't see it as an error — this flag carries the already-handled halt to push-stream.js's
// ordered ingest, which stops applying further messages. Sticky until reload (or resetState).
export function isChainHalted() {
  return state.chainHalted;
}
export function setChainHalted(v) {
  state.chainHalted = v;
}

// --- Bid prompt projection override (interaction layer) ---

// Written by bidding/bid-pipeline.js, read by prompt-ui's renderBidPrompt and
// board-layout.js's refreshLayout geometry fold: { value, punctuation, dismissed } | null. While set, the prompt
// zone paints the pipeline's projection (latest server truth + queued clicks) instead of
// the replay state, so the displayed value never flickers backwards as bursts catch up.
export function getBidPromptOverride() {
  return state.bidPromptOverride;
}
export function setBidPromptOverride(override) {
  state.bidPromptOverride = override;
}

// --- Dispatch replay backlog (pendingDispatchEvents) ---

// Callback injection (set once via initBidPipeline): fired when the dispatch tail has
// fully drained — pendingDispatchEvents back to zero at a burst's end — so the bid
// pipeline can release its prompt override against the settled board. Mirrors the
// onAnimationSettled idiom above.
let onDispatchEventsDrained = null;
export function setOnDispatchEventsDrained(fn) {
  onDispatchEventsDrained = fn;
}

export function getPendingDispatchEvents() {
  return state.pendingDispatchEvents;
}
export function addPendingDispatchEvents(n) {
  state.pendingDispatchEvents += n;
}
/** Deliberately does not fire the drained callback — drainBurst notifies from its
 *  `finally` via notifyIfDispatchEventsDrained(), AFTER the resting commit, so the
 *  pipeline's release repaint wins over the commit's stale re-arm at a live bid duel.
 *  Once the auction has resolved the release suppresses that repaint so the commit's
 *  hand-decision look-ahead stands instead (bid-pipeline.js#maybeReleaseOverride). */
export function decrementPendingDispatchEvents(n = 1) {
  state.pendingDispatchEvents = Math.max(0, state.pendingDispatchEvents - n);
}
export function notifyIfDispatchEventsDrained() {
  if (state.pendingDispatchEvents === 0) onDispatchEventsDrained?.();
}

// --- Discard selection ---

export function getDiscardSelection() {
  return state.discardSelection;
}

// --- Bid animation ---

export function getBidAnimatedCount() {
  return state.bidAnimatedCount;
}
export function setBidAnimatedCount(n) {
  state.bidAnimatedCount = n;
}

// --- Passed positions (bidding ceremony) ---

export function getPassedPositions() {
  return state.passedPositions;
}

// --- Active bid numbers (persistent numbers surviving innerHTML='') ---

export function getActiveBidNumbers() {
  return state.activeBidNumbers;
}
export function setActiveBidNumber(position, value, punctuation) {
  state.activeBidNumbers.set(position, { value, punctuation });
}
export function removeActiveBidNumber(position) {
  state.activeBidNumbers.delete(position);
}

// --- Hint timer ---

export function getHintTimer() {
  return state.hintTimer;
}
export function setHintTimer(id) {
  state.hintTimer = id;
}

// --- Pending slot fade-in (gates .slot-fade-in class on trick slots — set during the
//     declaration pause so the first post-bidding render lets slots materialize). ---

export function isPendingSlotFadeIn() {
  return state.pendingSlotFadeIn;
}
export function setPendingSlotFadeIn(v) {
  state.pendingSlotFadeIn = v;
}

// --- Prompt commit transaction (prevents contradictory clicks, carries inline error state) ---

export function isPromptCommitPending() {
  return state.promptCommit.pending;
}
export function getPromptCommitError() {
  return state.promptCommit.error;
}
export function beginPromptCommit() {
  if (state.promptCommit.pending) return false;
  state.promptCommit.pending = true;
  state.promptCommit.error = null;
  return true;
}
export function finishPromptCommit() {
  state.promptCommit.pending = false;
  state.promptCommit.error = null;
}
export function failPromptCommit(messageKey = "promptActionFailed") {
  state.promptCommit.pending = false;
  state.promptCommit.error = messageKey;
}

// --- API error tracking (for session loss detection) ---

export function hadApiError() {
  return state.hadApiError;
}
export function setHadApiError(v) {
  state.hadApiError = v;
}

// --- Trump reveal transition (declaration animation) ---

export function isTrumpRevealPending() {
  return state.trumpRevealPending;
}
export function setTrumpRevealPending(v) {
  state.trumpRevealPending = v;
}

// --- Intentional session clear (suppresses error recovery during auto-play exit) ---

export function isIntentionalClear() {
  return state.intentionalClear;
}
export function setIntentionalClear(v) {
  state.intentionalClear = v;
}

// --- Composite resets ---

export function resetBidState() {
  state.bidAnimatedCount = 0;
  state.passedPositions.clear();
  state.activeBidNumbers.clear();
}

export function resetState() {
  state.app = null;
  state.currentState = null;
  state.previousState = null;
  state.animating = false;
  state.discardSelection.clear();
  state.bidAnimatedCount = 0;
  state.passedPositions.clear();
  state.activeBidNumbers.clear();
  state.hintTimer = null;
  state.pendingSlotFadeIn = false;
  state.promptCommit.pending = false;
  state.promptCommit.error = null;
  state.hadApiError = false;
  state.intentionalClear = false;
  state.trumpRevealPending = false;
  state.dispatchTailClaims = 0;
  // Settle, don't just drop: a suite that reset mid-wait would otherwise leave that test hanging
  // on a promise nothing can ever resolve (the tail is free by the line above, after all).
  settleDispatchTailIdleWaiters();
  state.chainHalted = false;
  state.bidPromptOverride = null;
  state.pendingDispatchEvents = 0;
  state.boardBurstShaped = false;
}
